/**
 * Structured error shape. Every non-2xx response in the app must be represented
 * as an `ApiError`; http.ts guarantees this invariant. UI layers should never
 * rely on a "silent empty body" to detect failure.
 *
 * `status: 0` signals a network / transport failure (DNS, timeout, abort, TLS
 * error, etc.). `code` is a stable machine-readable string — callers use it
 * for switch-based branching; `message` is for logging only (never show in UI —
 * it may include PHI from downstream services).
 */

export type ApiErrorCode =
  | 'network_error'
  | 'timeout'
  | 'aborted'
  | 'bad_request'
  | 'validation_failed'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'unprocessable'
  | 'rate_limited'
  | 'server_error'
  | 'invalid_response'
  | 'unknown';

/**
 * One field-level violation surfaced by the backend's bean-validation handler. Unlike
 * `ApiError.message` (which may include downstream PHI and must never be rendered), these
 * messages are framework-generated by Hibernate Validator and are safe to show to the user.
 * They only ever ride along when the response carries `code === 'VALIDATION_FAILED'`.
 */
export interface FieldViolation {
  /** Leaf name of the offending request param / body field, e.g. `"lastName"`. */
  readonly field: string;
  /** User-facing constraint message, e.g. `"Last name must be between 2 and 200 characters"`. */
  readonly message: string;
}

export interface ApiErrorInit {
  status: number;
  code: ApiErrorCode;
  message: string;
  correlationId?: string;
  fieldViolations?: readonly FieldViolation[];
  userFacingDetail?: string;
  /**
   * The backend contract `code` string verbatim (e.g. `account_pending`),
   * preserved even when it isn't a member of {@link ApiErrorCode} (which
   * collapses to the generic status bucket). Used for status-specific branching
   * such as the PTV2-1424 account-status gate.
   */
  contractCode?: string;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly correlationId?: string;
  readonly fieldViolations?: readonly FieldViolation[];
  /**
   * User-facing `detail` string pulled from a JSON:API-shaped error entry.
   * Distinct from `message` because JSON:API defines `detail` as the
   * intentionally user-visible description (e.g. "Person already belongs to
   * family GUID::..."), while `message` may include arbitrary text from
   * downstream services and is unsafe to render.
   */
  readonly userFacingDetail?: string;

  /** The backend contract `code` verbatim (see {@link ApiErrorInit.contractCode}). */
  readonly contractCode?: string;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    if (init.correlationId !== undefined) this.correlationId = init.correlationId;
    if (init.fieldViolations !== undefined && init.fieldViolations.length > 0) {
      this.fieldViolations = init.fieldViolations;
    }
    if (init.userFacingDetail !== undefined && init.userFacingDetail.length > 0) {
      this.userFacingDetail = init.userFacingDetail;
    }
    if (init.contractCode !== undefined && init.contractCode.length > 0) {
      this.contractCode = init.contractCode;
    }
  }

  toJSON(): {
    status: number;
    code: ApiErrorCode;
    message: string;
    correlationId?: string;
    fieldViolations?: readonly FieldViolation[];
    userFacingDetail?: string;
  } {
    return {
      status: this.status,
      code: this.code,
      message: this.message,
      ...(this.correlationId !== undefined ? { correlationId: this.correlationId } : {}),
      ...(this.fieldViolations !== undefined ? { fieldViolations: this.fieldViolations } : {}),
      ...(this.userFacingDetail !== undefined ? { userFacingDetail: this.userFacingDetail } : {}),
    };
  }
}

export function mapStatusToCode(status: number): ApiErrorCode {
  if (status === 0) return 'network_error';
  if (status === 400) return 'bad_request';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 408) return 'timeout';
  if (status === 409) return 'conflict';
  if (status === 422) return 'unprocessable';
  if (status === 429) return 'rate_limited';
  if (status >= 500 && status < 600) return 'server_error';
  if (status >= 400 && status < 500) return 'bad_request';
  return 'unknown';
}

function isErrorBody(
  value: unknown,
): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null;
}

/**
 * Live PhenoTips returns errors in a JSON:API-style envelope:
 * `{ "errors": [{ "status": "400", "title": "Bad Request", "detail": "..." }] }`.
 * Pull the first entry so `pickErrorBodyMessage` / `pickRawErrorCodeString` can
 * read its fields the same way they read top-level shapes.
 */
function firstErrorEntry(
  b: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> | undefined {
  const { errors } = b;
  if (!Array.isArray(errors) || errors.length === 0) return undefined;
  const first: unknown = errors[0];
  return isErrorBody(first) ? first : undefined;
}

function pickRawErrorCodeString(
  b: Readonly<Record<string, unknown>>,
): string | undefined {
  const { code, error, error_code: errorCode } = b;
  if (typeof code === 'string') return code;
  if (typeof errorCode === 'string') return errorCode;
  if (typeof error === 'string') return error;
  const entry = firstErrorEntry(b);
  if (entry !== undefined) {
    const nested = pickRawErrorCodeString(entry);
    if (nested !== undefined) return nested;
  }
  return undefined;
}

function pickErrorBodyMessage(
  b: Readonly<Record<string, unknown>>,
  fallback: string,
): string {
  const { message, detail, title } = b;
  if (typeof message === 'string' && message.length > 0) {
    return message;
  }
  if (typeof detail === 'string' && detail.length > 0) {
    return detail;
  }
  if (typeof title === 'string' && title.length > 0) {
    return title;
  }
  const entry = firstErrorEntry(b);
  if (entry !== undefined) {
    return pickErrorBodyMessage(entry, fallback);
  }
  return fallback;
}

function pickErrorBodyCorrelationId(
  b: Readonly<Record<string, unknown>>,
): string | undefined {
  const { correlationId, correlation_id, trace_id: traceId } = b;
  if (typeof correlationId === 'string') return correlationId;
  if (typeof correlation_id === 'string') return correlation_id;
  if (typeof traceId === 'string') return traceId;
  const entry = firstErrorEntry(b);
  if (entry !== undefined) {
    return pickErrorBodyCorrelationId(entry);
  }
  return undefined;
}

/**
 * Pulls out every `errors[*]` entry whose `code === "VALIDATION_FAILED"` and turns it into a
 * {@link FieldViolation}. The backend's `PhenoTipsExceptionHandler` populates this code on every
 * Bean Validation failure (constraint on @RequestParam/@PathVariable as well as @RequestBody) and
 * trims the property path to its leaf, so `sourcePointer` is already the field id we want.
 */
function extractFieldViolations(
  b: Readonly<Record<string, unknown>>,
): readonly FieldViolation[] {
  const { errors } = b;
  if (!Array.isArray(errors)) return [];
  const out: FieldViolation[] = [];
  for (const entry of errors) {
    if (!isErrorBody(entry)) continue;
    if (entry.code !== 'VALIDATION_FAILED') continue;
    const field = typeof entry.sourcePointer === 'string' ? entry.sourcePointer : null;
    const message = typeof entry.detail === 'string' ? entry.detail : null;
    if (field === null || message === null || field.length === 0 || message.length === 0) continue;
    out.push({ field, message });
  }
  return out;
}

/**
 * Best-effort extraction of error details from an unknown response body.
 * Never throws — if the body is garbage we just fall back to defaults.
 *
 * Accepts two JSON:API-adjacent shapes the live BE returns:
 *   - `{ errors: [{ status, title, detail }, ...] }` — the envelope form
 *   - `[{ status, title, detail }, ...]` — a bare top-level array (business
 *     validation on some endpoints returns this form)
 * The bare-array case is normalised to the envelope so the same field
 * extraction path handles both.
 */
export function extractErrorDetails(
  body: unknown,
  status: number,
): {
  code: ApiErrorCode;
  message: string;
  correlationId?: string;
  fieldViolations?: readonly FieldViolation[];
  userFacingDetail?: string;
  contractCode?: string;
} {
  const fallbackCode = mapStatusToCode(status);
  const fallbackMessage = `HTTP ${String(status)}`;

  const normalized: unknown = Array.isArray(body) ? { errors: body } : body;

  if (!isErrorBody(normalized)) {
    return { code: fallbackCode, message: fallbackMessage };
  }

  const b = normalized;
  const raw = pickRawErrorCodeString(b);
  let code: ApiErrorCode = fallbackCode;
  if (isApiErrorCode(raw)) {
    code = raw;
  }
  let message = pickErrorBodyMessage(b, fallbackMessage);
  const correlationId = pickErrorBodyCorrelationId(b);
  const userFacingDetail = pickUserFacingDetail(b);

  // Promote a generic 4xx into the more specific `validation_failed` code whenever the envelope
  // carries the backend's stable `VALIDATION_FAILED` marker. This is the *only* path that lets
  // user-facing rendering bypass the privacy gate above on `message` — the per-field strings are
  // framework-generated by Hibernate Validator (e.g. @Size message templates), not downstream
  // service messages, so they're safe to show.
  const fieldViolations = extractFieldViolations(b);
  if (fieldViolations.length > 0) {
    code = 'validation_failed';
    message = `Validation failed for: ${fieldViolations.map((v) => v.field).join(', ')}`;
  }

  return {
    code,
    message,
    ...(correlationId !== undefined ? { correlationId } : {}),
    ...(fieldViolations.length > 0 ? { fieldViolations } : {}),
    ...(userFacingDetail !== undefined ? { userFacingDetail } : {}),
    ...(raw !== undefined ? { contractCode: raw } : {}),
  };
}

/**
 * Extract a user-safe `detail` string from a JSON:API-shaped error entry.
 * Unlike `pickErrorBodyMessage` (which returns *any* message-ish field), this
 * only returns a value when the body is a well-formed JSON:API errors envelope
 * with a `detail` string on the first entry — so callers can render it in the
 * UI without leaking arbitrary downstream error text.
 */
function pickUserFacingDetail(
  b: Readonly<Record<string, unknown>>,
): string | undefined {
  const entry = firstErrorEntry(b);
  if (entry === undefined) return undefined;
  const { detail } = entry;
  if (typeof detail !== 'string') return undefined;
  const trimmed = detail.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

const API_ERROR_CODES: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>([
  'network_error',
  'timeout',
  'aborted',
  'bad_request',
  'validation_failed',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'unprocessable',
  'rate_limited',
  'server_error',
  'invalid_response',
  'unknown',
]);

function isApiErrorCode(value: unknown): value is ApiErrorCode {
  if (typeof value !== 'string') return false;
  for (const code of API_ERROR_CODES) {
    if (code === value) {
      return true;
    }
  }
  return false;
}

/**
 * Machine-readable code for `ErrorCallout` / `FetchErrorCallout` (never use raw
 * `Error.message` in the UI).
 */
export function getErrorCodeForUi(error: unknown): string {
  return error instanceof ApiError ? error.code : 'unknown';
}

/**
 * Returns the field violations attached to an error (if any). Empty array for non-validation
 * errors, non-`ApiError` instances, or `ApiError`s without a `fieldViolations` payload — so
 * callers can unconditionally `.map(...)` over the result without a null-check guard.
 */
export function getFieldViolations(error: unknown): readonly FieldViolation[] {
  if (!(error instanceof ApiError)) return [];
  return error.fieldViolations ?? [];
}

/**
 * Backend account-status contract codes (PTV2-1424, DESIGN §9.2). The core
 * `AccountStatusFilter` returns one of these on a 403 for a non-ACTIVE
 * principal hitting any non-`/me` path. `account_inactive` covers any other
 * (future / unrecognised) non-active status.
 */
export type AccountStatusCode = 'account_pending' | 'account_deactivated' | 'account_inactive';

const ACCOUNT_STATUS_CODES: ReadonlySet<string> = new Set<AccountStatusCode>([
  'account_pending',
  'account_deactivated',
  'account_inactive',
]);

/** Whether `error` is a 403 carrying an account-status contract code. */
export function getAccountStatusCode(error: unknown): AccountStatusCode | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status !== 403) return null;
  const raw = error.contractCode;
  return raw !== undefined && ACCOUNT_STATUS_CODES.has(raw) ? (raw as AccountStatusCode) : null;
}
